import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { api } from '../api'
import peekMascot from '../assets/chu-peek.png'
// The slot rule lives in `charts`, and there is one of it. An atom that drew
// its own categorical colours would be a second palette policy in the same
// board, which is how two panels start disagreeing about what a colour means.
import { NEUTRAL_SERIES, slotColor } from './charts'
import type { ArcadeTheme } from './themes'
import type { Mover as MoverModel, Surface as SurfaceModel, TopicCount } from './viewModel'

/**
 * The arcade's shared presentation atoms.
 *
 * Extracted from `pages/Leaderboard.tsx` in v0.15.0 so that every layout
 * skeleton, every drill-down and the offscreen share-card renderer draw a
 * player the same way. Before the split there was one markup tree and one copy
 * of each of these; with several skeletons, a second copy of the avatar or the
 * sparkline is how two variants start disagreeing about what a person looks
 * like.
 *
 * Everything here is themed through the `--a-*` custom properties the arcade
 * root sets, so an atom never knows which theme it is inside.
 */

/**
 * The rank-move marker: `▲2` or `▼1`, or nothing at all.
 *
 * ONE component for all seven skeletons, for the same reason there is one
 * `PixelAvatar` — a second copy is how two layouts start disagreeing about
 * what a mover looks like, and a mover that means different things in two
 * places is worse than no mover.
 *
 * Two properties this component is responsible for holding:
 *
 *  1. It renders NOTHING for the withheld variant. It cannot do otherwise:
 *     `MoverModel`'s `{ shown: false }` arm has no delta on it, so there is no
 *     value here to fall back on and no `?? 0` to write. Under hide-costs
 *     every player gets that variant (`viewModel.mover()`), so no `.mover`
 *     element exists anywhere in the tree — which is exactly what the
 *     invariant test asserts.
 *  2. The glyph NEVER appears without its magnitude. `▲`/`▼` are reserved
 *     arcade-wide for rank moves — the ladder cut-line uses `◆`, drill links
 *     use `→` — because a bare `▲` on a rule cannot be told apart from "moved
 *     up 2". The text comes pre-formatted from the seam, so this component
 *     has no way to emit a naked arrow.
 *
 * `aria-label` spells the delta out, since "▲2" read aloud is a triangle.
 */
export function Mover({ mover }: { mover: MoverModel }) {
  if (!mover.shown) return null
  return (
    <span
      className={`mover ${mover.dir}`}
      data-move={mover.dir}
      aria-label={`${mover.dir} ${mover.places} place${mover.places === 1 ? '' : 's'}`}
    >
      {mover.text}
    </span>
  )
}

/**
 * THE SURFACE BREAKDOWN under a row's score: what the total is MADE OF.
 *
 * WHY THIS IS ON THE BOARD AT ALL. On 10 Sep 2026 one developer showed 1.0M
 * tokens against a chat user's 14.8K, with nothing on screen to say those are
 * different behaviours reached through different doors. The board was not
 * wrong, it was unreadable. So the total stays the headline and still ranks
 * the row, and this says where it came from.
 *
 * ONE component for every skeleton, the same reason there is one `Mover`: a
 * second copy is how two layouts start disagreeing about what a breakdown
 * means, and a breakdown that means two things is worse than none.
 *
 * THREE PROPERTIES IT IS RESPONSIBLE FOR HOLDING.
 *
 *  1. IT RENDERS NOTHING WHEN THE SPLIT IS UNKNOWN, and it cannot do
 *     otherwise: `Surface`'s `{ known: false }` arm carries no figures, so
 *     there is no value here to fall back on and no `?? 0` to write. That is
 *     the whole reason the seam is a union — a `0` in this slot would read as
 *     "wrote no code", which is a claim about a person the board never made.
 *     The board prints its one honest note elsewhere; a row says nothing.
 *  2. NO `+` ANYWHERE, and no arithmetic a reader could do wrong. `·`
 *     separates the halves exactly as the ladder's `department · streak` line
 *     already does, because these two ARE the number above them. The
 *     `keySpend` slot this replaced printed `+ $12.00 key`, which invited a
 *     reader to add developer dollars onto a total that already contained
 *     them.
 *  3. A HALF WITH NOTHING IN IT IS NOT PRINTED. Somebody who has never opened
 *     Claude Code should not carry `CODE 0` on every row of every window; the
 *     absence of the label is the honest rendering, and it is only available
 *     because "none" and "not known" are now different states here.
 *
 * The tokens are the everyday reading and always show. The cost half rides
 * along only when the board is showing costs at all — `Figure.withheld` is
 * true for both halves under hide-costs, and printing `$▓▓▓ · $▓▓▓` in a
 * six-point sub-line is noise rather than an affordance (the row's own cost
 * slot already carries the "there is a number here you cannot see" signal).
 */
export function SurfaceBreakdown({ surface, tokensOnly = false }: { surface: SurfaceModel; tokensOnly?: boolean }) {
  if (!surface.known) return null
  if (!surface.hasChat && !surface.hasCode) return null
  // `tokensOnly` (round 11, the staff portal's Home boards): forces the
  // token-only reading regardless of whether cost figures are actually
  // available -- a caller-level presentation choice, never a proxy for
  // hide-costs, which stays the ONLY thing that can make a figure
  // genuinely unavailable rather than merely unwanted on this one screen.
  const costs = !tokensOnly && !surface.chatCost.withheld && !surface.codeCost.withheld
  return (
    <span className="surfsplit" data-surface-split="1">
      {surface.hasChat ? (
        <span className="sf chat">
          <i>CHAT</i> {surface.chatTokensText}
          {costs ? <em>{surface.chatCost.text}</em> : null}
        </span>
      ) : null}
      {surface.hasChat && surface.hasCode ? <span className="sfsep">·</span> : null}
      {surface.hasCode ? (
        <span className="sf code">
          <i>CLAUDE CODE</i> {surface.codeTokensText}
          {costs ? <em>{surface.codeCost.text}</em> : null}
        </span>
      ) : null}
    </span>
  )
}

/**
 * The board's ONE statement that it could not compute the breakdown.
 *
 * Board level, not row level, because that is the level the fact lives at:
 * the panel could not read the developer-key roster, which is a property of
 * this render and not of any person on it. Repeating it per row would turn
 * one honest sentence into a hundred, and putting it in a row's breakdown slot
 * would put a claim about infrastructure where a claim about a person goes.
 *
 * It says explicitly that the totals are unaffected, because the reflex on
 * seeing a caveat over a leaderboard is to distrust the ranking — and the
 * ranking is exactly what did not change.
 */
export function SurfaceSplitNote({ shown }: { shown: boolean }) {
  if (shown) return null
  return (
    <p className="splitnote" data-surface-note="1">
      Chat / Claude Code breakdown unavailable this refresh — the developer-key list could not be read, so the
      two surfaces cannot be told apart. Totals and ranking are unaffected.
    </p>
  )
}

/** The deterministic 8-bit identicon math shared by the live `PixelAvatar`
 *  canvas and the offscreen profile-card PNG renderer, so "share card" always
 *  matches the avatar shown on the board. */
export function avatarSprite(name: string): { hue: number; on: boolean[][] } {
  let h = 2166136261
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const hue = Math.abs(h) % 360
  const on: boolean[][] = []
  for (let y = 0; y < 5; y++) {
    const row: boolean[] = new Array(5).fill(false)
    for (let x = 0; x < 3; x++) {
      // Deterministic bit from the hash; mirror left<->right for symmetry.
      if (((h >> (y * 3 + x)) & 1) === 1) {
        row[x] = true
        row[4 - x] = true
      }
    }
    on.push(row)
  }
  return { hue, on }
}

/** The shape `backend/src/routes/leaderboard.ts` `opaqueId()` produces: the
 *  first 12 hex characters of a salted sha256. Anything else is not an id this
 *  arcade issued, and is never built into a URL. */
const OPAQUE_ID = /^[0-9a-f]{12}$/

/**
 * The photo endpoint for one player, keyed on their OPAQUE id.
 *
 * A separate, exported, pure function on purpose: it is the single place a
 * photo URL is constructed anywhere in the arcade, and the invariant test
 * asserts against it directly. The id is the same salted, non-reversible
 * sha256 the board already keys every drill-down on, so no address is ever
 * built into a URL, an attribute, or the DOM.
 */
export function photoUrl(id: string, window: string): string {
  // REFUSES anything that is not an opaque id, rather than encoding it and
  // hoping the server rejects it. Percent-encoding an address still leaves the
  // address in the URL, in the DOM and in the request log -- readable to
  // anyone who looks. An id that does not match the digest shape cannot be
  // turned into a photo URL at all, and the caller falls back to the
  // identicon, which is already the correct rendering for "no photo".
  if (!OPAQUE_ID.test(id)) return ''
  return `/api/arcade/photo/${id}?window=${encodeURIComponent(window)}`
}

/**
 * A player's avatar: their real Entra photo, quantised to the arcade, or a
 * deterministic identicon when there is no photo.
 *
 * WHY A CANVAS AND NOT AN `<img>`. Shrinking a JPEG with CSS just draws it
 * small; it stays a photograph. Drawing it into a tiny backing store
 * (12x12 on a board row, 20x20 in the person panel) genuinely RESAMPLES it to
 * that many pixels, and `image-rendering: pixelated` then blows those pixels
 * up as blocks. That is the 80s treatment: a real face, rendered as a sprite.
 *
 * THE FALLBACK IS THE OLD BEHAVIOUR, UNCHANGED. Graph returns nothing for a
 * person with no photo and the route answers 204, which is ordinary rather
 * than an error. Those players keep the deterministic identicon the board has
 * always drawn, so "no photo" was solved before this feature existed and stays
 * solved. Any failure — 204, a network error, a decode error — lands there.
 *
 * DATA MINIMISATION. `photoId` is an opaque id and `name` is a derived display
 * name. Neither is an address, and the element this renders is a `<canvas>`
 * with no `src` attribute at all, so nothing resembling an identifier reaches
 * the DOM by any path.
 */
export function PixelAvatar({
  name,
  size = 34,
  className,
  photoId,
  window: win,
  grid,
}: {
  name: string
  size?: number
  className?: string
  /** Opaque player id. Omit to force the identicon (offscreen renderers,
   *  tests, and anywhere no player is in scope). */
  photoId?: string
  /** The board's current window, forwarded so the route resolves the id
   *  against the same population the viewer is looking at. */
  window?: string
  /** Backing-store resolution. Defaults to a size-appropriate grid: coarse
   *  enough on a 26px row to read as a sprite, fine enough at 76px to read as
   *  a face. */
  grid?: number
}) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  // Identicons are 5x5 by construction; a photo gets a grid scaled to how big
  // it will actually be drawn.
  const cells = grid ?? Math.max(10, Math.min(28, Math.round(size / 3.4)))

  useEffect(() => {
    const c = ref.current
    if (!c) return
    let cancelled = false

    const drawIdenticon = () => {
      const ctx = c.getContext('2d')
      if (!ctx) return
      c.width = 5
      c.height = 5
      const { hue, on } = avatarSprite(name)
      ctx.clearRect(0, 0, 5, 5)
      ctx.fillStyle = `hsl(${hue} 90% 60%)`
      for (let y = 0; y < 5; y++) {
        for (let x = 0; x < 5; x++) {
          if (on[y]?.[x]) ctx.fillRect(x, y, 1, 1)
        }
      }
    }

    if (!photoId) {
      drawIdenticon()
      return
    }

    // The identicon is drawn FIRST, always. It is what shows while the photo
    // is in flight, and what stays if there is not one.
    drawIdenticon()
    const url = photoUrl(photoId, win ?? 'mo')
    if (url === '') return

    // THE PHOTO MUST BE FETCHED, NOT SET AS AN `<img src>`. The arcade
    // authenticates with a Bearer token held in JS; an `<img>` sends cookies
    // and nothing else, so every photo came back 401 the first time this
    // shipped. Going through `api()` attaches the operator's token the same
    // way every other call in the panel does -- and it keeps the 204 "no
    // photo" answer as an ordinary result rather than an error.
    void (async () => {
      try {
        const res = await api(url)
        // 204 = Graph has no photo for this person. Ordinary, not a failure.
        if (cancelled || !res || res.status === 204) return
        const blob = await res.blob()
        if (cancelled || blob.size === 0) return
        const bitmap = await createImageBitmap(blob)
        if (cancelled) return
        const ctx = c.getContext('2d')
        if (!ctx) return
        c.width = cells
        c.height = cells
        ctx.clearRect(0, 0, cells, cells)
        // Square-crop from the centre before resampling, so a portrait is not
        // squashed into the cell grid.
        const side = Math.min(bitmap.width, bitmap.height)
        if (side <= 0) return
        ctx.drawImage(
          bitmap,
          (bitmap.width - side) / 2,
          (bitmap.height - side) / 2,
          side,
          side,
          0,
          0,
          cells,
          cells,
        )
        bitmap.close?.()
      } catch {
        // A 401/403/500, an undecodable body, or a runtime with no
        // createImageBitmap (jsdom) all land on the identicon that is already
        // on the canvas. An avatar is never worth a broken board.
        if (!cancelled) drawIdenticon()
      }
    })()

    return () => {
      cancelled = true
    }
  }, [name, photoId, win, cells])

  return (
    <canvas
      ref={ref}
      width={5}
      height={5}
      aria-hidden="true"
      className={className}
      style={{
        width: size,
        height: size,
        imageRendering: 'pixelated',
        border: '1px solid var(--a-line)',
        flexShrink: 0,
        background: 'var(--a-avatar-bg)',
      }}
    />
  )
}

/**
 * A tiny CRT heartbeat: daily activity as a filled area sparkline.
 *
 * FLUID as of v0.15.0. It used to render at a fixed `width={108}`, which on a
 * phone was wider than the ~102px the player cell actually had, so the board's
 * `overflow: hidden` clipped it on every row. It now scales to whatever box it
 * is given via `viewBox` + `preserveAspectRatio="none"`, which fixes the
 * clipping everywhere at once and lets the mobile row promote the heartbeat to
 * a full-width band under the name.
 */
export function Sparkline({ data, height = 20 }: { data: number[]; height?: number }) {
  if (!data || data.length === 0) return null
  const max = Math.max(...data, 1)
  // Kept as the viewBox coordinate space, not a px width: the rendered size
  // comes from CSS.
  const w = 108
  const h = 20
  const step = data.length > 1 ? w / (data.length - 1) : w
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 2) - 1).toFixed(1)}`)
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      className="spark"
      style={{ display: 'block', width: '100%', height, marginTop: 4 }}
    >
      <polyline points={`0,${h} ${pts.join(' ')} ${w},${h}`} fill="var(--a-spark-fill)" stroke="none" />
      <polyline points={pts.join(' ')} fill="none" stroke="var(--a-accent)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

/** Topic bars — org-wide (Taxonomy mode) or one team's own (Team drill-down).
 *  Same rendering either way so "what is this used for" always reads the same
 *  visual language. When `onSelect` is supplied, each bar opens that topic's
 *  drill-down. */
export function TopicBars({
  topics,
  colors,
  onSelect,
}: {
  topics: TopicCount[]
  colors: string[]
  onSelect?: (topic: string) => void
}) {
  if (topics.length === 0) return <p className="hint">NO TOPIC DATA IN THIS WINDOW.</p>
  const max = topics[0]?.count || 1
  return (
    <div className="teams">
      {topics.map((t, i) => (
        <div
          className={`team${onSelect ? ' clickable' : ''}`}
          key={t.topic}
          style={{ ['--pos' as string]: colors[i % colors.length] }}
          role={onSelect ? 'button' : undefined}
          tabIndex={onSelect ? 0 : undefined}
          onClick={onSelect ? () => onSelect(t.topic) : undefined}
          onKeyDown={
            onSelect
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(t.topic)
                  }
                }
              : undefined
          }
        >
          <div className="tl">
            <span className="fac">{t.topic}</span>
            <span className="amt">{t.count.toLocaleString()}</span>
          </div>
          <div className="track">
            <div className="fill" style={{ width: `${(t.count / max) * 100}%` }} />
          </div>
          {onSelect ? <div className="drill">VIEW TOPIC →</div> : null}
        </div>
      ))}
    </div>
  )
}

/**
 * A person's own taxonomy as ONE segmented share bar plus a legend.
 *
 * WHY NOT `TopicBars`. That component draws a full-width bar per topic, which
 * is right for the org-wide Taxonomy view — those bars are the content of the
 * page and there is room for them. Inside the person panel the same treatment
 * produced a dozen stacked bars that ran off the bottom of the screen, and it
 * answered the wrong question: independent bars invite you to compare one
 * topic against another, when what somebody wants from their own breakdown is
 * what SHARE of their work each thing took. A single 100%-wide segmented bar
 * says that in one row instead of twelve, and the legend underneath still
 * carries the counts for anyone who wants them.
 *
 * The tail is pooled into one "N more" segment rather than rendered as a row
 * of slivers too thin to see or read.
 */
export function TopicShareBar({
  topics,
  colors,
  named = 5,
}: {
  topics: TopicCount[]
  colors: string[]
  /** How many topics get their own segment before the rest are pooled. */
  named?: number
}) {
  if (topics.length === 0) return <p className="hint">NO TOPIC DATA IN THIS WINDOW.</p>
  const total = topics.reduce((n, t) => n + t.count, 0)
  if (total <= 0) return <p className="hint">NO TOPIC DATA IN THIS WINDOW.</p>

  const head = topics.slice(0, named)
  const tail = topics.slice(named)
  const tailCount = tail.reduce((n, t) => n + t.count, 0)
  const segments = [
    // Fixed slot order, and NEVER cycled: past the palette a segment takes the
    // neutral rather than wrapping to hue zero, which would put one colour on
    // two different topics in the same bar.
    ...head.map((t, i) => ({ label: t.topic, count: t.count, color: slotColor(colors, i) })),
    ...(tailCount > 0
      ? [
          {
            label: `${tail.length} more`,
            count: tailCount,
            /**
             * THE NEUTRAL, not the last palette entry.
             *
             * This used to take `colors[colors.length - 1]` on the reasoning
             * that the last entry is "the muted one in every theme". That
             * stopped being true when the themes moved to the VALIDATED SERIES
             * palette in v0.20.0: its sixth slot is a full-saturation red, so
             * the pooled tail was drawn as a named category competing with the
             * real ones — and at `named = 6`, which the taxonomy table passes,
             * it was drawn in the SAME colour as the sixth topic. One hue
             * standing for two entities is the exact defect the slot rule
             * exists to prevent. The donut has always used the neutral here;
             * this is the bar catching up with it.
             */
            color: NEUTRAL_SERIES,
          },
        ]
      : []),
  ]

  return (
    <div className="tshare">
      <div className="tsbar" role="img" aria-label={`Topic share across ${topics.length} topics`}>
        {segments.map((seg) => (
          <i
            key={seg.label}
            style={{ width: `${(seg.count / total) * 100}%`, background: seg.color }}
            title={`${seg.label} — ${seg.count.toLocaleString()}`}
          />
        ))}
      </div>
      <div className="tslegend">
        {segments.map((seg) => (
          <div key={seg.label}>
            <em style={{ background: seg.color }} />
            <span>{seg.label}</span>
            <b>{seg.count.toLocaleString()}</b>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ==========================================================================
 * HEADER (moved from `pages/Leaderboard.tsx`, 23 Sep 2026, round 10)
 * `PixelLogo` and `ArcadeHeader` so the staff portal's Home tab can carry the
 * same wordmark as the board it sits beside, with different words -- the
 * same reuse-not-copy rule every atom on this page already follows.
 * ======================================================================== */

/** The CHU peek mascot rendered as chunky 8-bit pixel art. */
export function PixelLogo({ theme }: { theme: ArcadeTheme }) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    const c = ref.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    const img = new Image()
    img.onload = () => {
      ctx.imageSmoothingEnabled = false
      ctx.clearRect(0, 0, c.width, c.height)
      ctx.drawImage(img, 0, 0, c.width, c.height)
    }
    img.src = peekMascot
  }, [])
  return (
    <canvas
      ref={ref}
      width={22}
      height={22}
      aria-hidden="true"
      className="pixlogo"
      style={{
        imageRendering: 'pixelated',
        // Only the neon worlds glow; the daylight and printed themes would
        // read as a rendering artefact with a halo behind the mascot.
        filter: theme.glow ? `drop-shadow(0 0 6px ${theme.accent})` : 'none',
        flexShrink: 0,
      }}
    />
  )
}

/**
 * The arcade's own masthead: mascot, eyebrow line, wordmark, and an optional
 * second line in the "coin" style (the blinking cursor). Reuses the SAME
 * `.brand`/`.eyebrow`/`.brandrow`/`.coin`/`.blink` classes `arcadeCss.ts`
 * already generates for the leaderboard's own header -- `title`/`eyebrow`/
 * `subtitle` are the only things that vary between callers, never the CSS.
 */
export function ArcadeHeader({
  theme,
  title,
  eyebrow,
  subtitle,
}: {
  theme: ArcadeTheme
  title: ReactNode
  eyebrow?: string
  subtitle?: string
}) {
  return (
    <div className="brand">
      {/* Optional since 24 Sep 2026: Maroun removed the small "CHU-GPT"
          line above the title from every portal page. Rendered only when a
          caller still asks for one. */}
      {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
      <div className="brandrow">
        <PixelLogo theme={theme} />
        <h1>{title}</h1>
      </div>
      {subtitle ? (
        <div className="coin">
          <span className="blink">▮</span> {subtitle}
        </div>
      ) : null}
    </div>
  )
}
